/**
 * Locale-aware formatters for DNW ERP.
 *
 * Locale: uk-UA. Money uses UAH (грн), volume is in м³.
 */

const VOLUME_FORMATTER = new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** Один знак після коми — для великих цифр дашборда і карток камер. */
const VOLUME_FORMATTER_COMPACT = new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const MONEY_FORMATTER = new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Кількість позиції замовлення. Дробів тут максимум три знаки, але хвіст із
 * нулів показувати не можна: бек віддає Decimal рядком («10.000»).
 */
const QUANTITY_FORMATTER = new Intl.NumberFormat('uk-UA', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Format a volume value in cubic metres, e.g. `12,350 м³`.
 *
 * `compact` leaves a single decimal (`12,4 м³`) — used for the large figures
 * on the dashboard and the chamber cards, where three decimals read as noise.
 */
export const formatVolume = (
  value: number | string | null | undefined,
  options?: { compact?: boolean },
): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  const formatter = options?.compact ? VOLUME_FORMATTER_COMPACT : VOLUME_FORMATTER;
  return `${formatter.format(n)} м³`;
};

/**
 * Format a monetary value in Ukrainian hryvnia, e.g. `1 250,00 грн`.
 *
 * Одиниця дописується словом навмисно: символ ₴ у системі не використовується
 * (рішення власника, `handoff/README.md` §2), а `style: 'currency'` ставить
 * саме його.
 */
export const formatMoney = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return `${MONEY_FORMATTER.format(n)} грн`;
};

/**
 * «Олександр Клименко» → «О. Клименко».
 *
 * Потрібно там, де прізвище стоїть у вузькій колонці списку і повне ім'я
 * переносить рядок навпіл.
 */
export const shortName = (full: string | null | undefined): string => {
  if (!full) return '—';
  const parts = full.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0][0]}. ${parts.slice(1).join(' ')}` : full;
};

/** Format a date as `dd.mm.yyyy`. Accepts Date, ISO string, or null. */
export const formatDate = (value: Date | string | null | undefined): string => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return DATE_FORMATTER.format(d);
};

/**
 * Кількість рядка замовлення разом з одиницею: `10 шт`, `235,5 м²`.
 *
 * Приходить рядком-Decimal («10.000», «235.500») — незначущі нулі прибираємо,
 * роздільник український (кома), як у `formatMoney`. Одиниця не зашита в код:
 * меблі рахують у штуках, підлогу й панелі — у м², погонаж — у мп, тож її
 * передає викликач (`OrderItem.unit`). Без одиниці — саме число.
 */
export const formatQty = (
  value: number | string | null | undefined,
  unit?: string | null,
): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return '—';
  const text = QUANTITY_FORMATTER.format(n);
  return unit ? `${text} ${unit}` : text;
};
